import { Fragment, type MouseEvent, useState } from 'react';
import { useTranslation } from 'react-i18next';

// ** MUI Components
import { styled } from '@mui/material/styles';
import Box from '@mui/material/Box';
import Collapse from '@mui/material/Collapse';
import List from '@mui/material/List';
import ListItemButton from '@mui/material/ListItemButton';
import ListItemIcon from '@mui/material/ListItemIcon';
import ListItemText from '@mui/material/ListItemText';
import Typography from '@mui/material/Typography';

// ** Components
import AdminOnly from '@erikdakoda/ui/components/AdminOnly';
import DraggableItem from '@erikdakoda/ui/components/DraggableItem';
import DropdownMenu from '@erikdakoda/ui/components/DropdownMenu';
import FaIconUvilo, { type IconPrefix } from '@erikdakoda/ui/components/FaIconUvilo';
import HoverListItem from '@erikdakoda/ui/components/HoverListItem';
import PowerIconButton from '@erikdakoda/ui/components/PowerIconButton';
import Spinner from '@erikdakoda/ui/components/Spinner';
import TextEditable from '@erikdakoda/ui/components/TextEditable';

// ** Hooks
import { useCurrentUser } from '@erikdakoda/ui/hooks/useCurrentUser';
import { useException } from '@erikdakoda/ui/hooks/useException';
import { useSnack } from '@erikdakoda/ui/hooks/useSnack';
import { useUpdateLabel } from '@erikdakoda/database/hooks/label';
import { restoreItems } from '@erikdakoda/ui/shared/restoreItems';
import { isLabelSectionOpen, useLabelCollapse } from '../hooks/useLabelCollapseStore';
import { useLabelSelection } from '../hooks/useLabelSelectionStore';
import { LabelSectionContext } from '../hooks/useLabelItemMenu';
import { itemDraggableId } from '../shared/labelDragIds';

// ** Types
import type { DraggableProvidedDragHandleProps } from '@hello-pangea/dnd';
import type {
  LabelItemListItemComponent,
  LabelItemRenderer,
  LabelSidebarProps,
  LabelSidebarSection,
} from '../types';

const OpenIcon = styled(FaIconUvilo)(({ theme }) => ({
  transform: 'rotate(0deg)',
  transition: theme.transitions.create(['transform'], {
    easing: theme.transitions.easing.easeOut,
    duration: theme.transitions.duration.standard,
  }),
  '&.open': {
    transform: 'rotate(90deg)',
  },
}));

export type LabelSectionProps = {
  section: LabelSidebarSection;
  /** Sidebar list id; selects the persisted collapse store. */
  listId: string;
  /** Dnd type of this sidebar's item drags. */
  droppableType: string;
  ItemComponent?: LabelItemListItemComponent;
  renderItem?: LabelItemRenderer;
  /** Renders rows without a `DraggableItem` wrapper. */
  dragDisabled?: boolean;
  /** Placeholder for a section with no rows. */
  emptyText?: string;
  /** Hides the default edit / trash header buttons. */
  disableSectionActions?: boolean;
  onRenameSection?: LabelSidebarProps['onRenameSection'];
  onTrashSection?: LabelSidebarProps['onTrashSection'];
  /** Opens the section editor; the consumer owns the `LabelEditor` mount. */
  onEditSection?: (sectionId: string) => void;
  dragHandleProps?: DraggableProvidedDragHandleProps | null;
  refetch?: () => Promise<void>;
};

/**
 * One collapsible sidebar section: a header row plus its draggable items. Section items arrive
 * already filtered — the shared name filter runs once, in `LabelSidebar`.
 */
export default function LabelSection({
  section,
  listId,
  droppableType,
  ItemComponent,
  renderItem,
  dragDisabled = false,
  emptyText,
  disableSectionActions = false,
  onRenameSection,
  onTrashSection,
  onEditSection,
  dragHandleProps,
  refetch,
}: LabelSectionProps) {
  'use memo';

  const { t } = useTranslation('label-ui');
  const { isAdmin } = useCurrentUser();
  const { snack } = useSnack();
  const exception = useException();
  const updateLabel = useUpdateLabel().mutateAsync;
  const { toggledSectionIds, toggleSection, openSection } = useLabelCollapse(listId);
  const { selectedSectionId, setSelectedSectionId } = useLabelSelection(listId);
  const [loadingDelete, setLoadingDelete] = useState(false);
  const [menuOpen, setMenuOpen] = useState(false);

  const open = isLabelSectionOpen(section.id, section.kind, toggledSectionIds);
  const selected = selectedSectionId === section.id;
  const isEmpty = section.items.length === 0;
  const itemsLoaded = section.itemsLoaded !== false;
  const itemsLoading = section.itemsLoading === true;
  const itemsDraggable = !dragDisabled && section.itemsDraggable !== false;
  const headerMenuItems = section.headerMenuItems;
  const renameEnabled = selected && !section.locked;

  const selectSection = (): void => {
    setSelectedSectionId(section.id);
    openSection(section.id, section.kind);
  };

  const handleTrashSection = async (event: MouseEvent<HTMLElement>): Promise<void> => {
    event.stopPropagation();
    setLoadingDelete(true);
    try {
      if (onTrashSection) {
        await onTrashSection({ section });
      } else {
        await updateLabel({ where: { id: section.id }, data: { trashedAt: new Date() } });
        const undo = async (): Promise<void> => {
          const { refusedIds = [] } = await restoreItems('Label', [section.id]);
          await refetch?.();
          if (refusedIds.length > 0) {
            throw new Error(t('folderRestoreFailed'));
          }
        };
        snack.success(t('folderDeleted'), { undo });
      }
    } catch (error: unknown) {
      exception(error);
    } finally {
      setLoadingDelete(false);
      await refetch?.();
    }
  };

  const handleEditSection = (event: MouseEvent<HTMLElement>): void => {
    if (!isAdmin) return;
    event.stopPropagation();
    onEditSection?.(section.id);
  };

  const setSectionName = async (name: string): Promise<void> => {
    try {
      if (onRenameSection) {
        await onRenameSection({ section, name });
        return;
      }
      const previousName = section.name;
      await updateLabel({ where: { id: section.id }, data: { name } });
      const undo = async (): Promise<void> => {
        await updateLabel({ where: { id: section.id }, data: { name: previousName } });
        await refetch?.();
      };
      snack.success(t('folderRenamed'), { undo });
    } catch (error: unknown) {
      exception(error);
    }
  };

  const handleToggle = (event: MouseEvent): void => {
    event.stopPropagation();
    toggleSection(section.id);
  };

  const handleSelectSection = (): void => {
    if (selected && open) {
      toggleSection(section.id);
      return;
    }
    selectSection();
  };

  const renderRow = (item: LabelSidebarSection['items'][number]) => {
    if (renderItem) {
      return renderItem({ item, section });
    }
    if (!ItemComponent || !item.row) {
      return null;
    }
    return <ItemComponent item={item.row} />;
  };

  const secondaryAction =
    headerMenuItems && headerMenuItems.length > 0 ? (
      <DropdownMenu
        className="label-section-dropdown"
        icon="ellipsis-stroke-vertical"
        items={[...headerMenuItems]}
        position="left"
        loading={section.headerActionLoading ?? false}
        setIsOpen={setMenuOpen}
      />
    ) : disableSectionActions || (section.locked && !isAdmin) ? null : (
      <>
        <AdminOnly>
          <PowerIconButton
            icon="pen-to-square"
            title={t('editFolder')}
            edge="end"
            aria-label="edit"
            size="large"
            data-item-id={section.id}
            onClick={handleEditSection}
          />
        </AdminOnly>
        <PowerIconButton
          icon="trash-can"
          title={t('trashFolder')}
          edge="end"
          aria-label="trash"
          size="large"
          data-item-id={section.id}
          onClick={handleTrashSection}
          loading={loadingDelete}
        />
      </>
    );

  return (
    <Box className="label-section">
      <LabelSectionContext.Provider value={{ section, selectSection }}>
        <HoverListItem
          className="subheader"
          forceHover={loadingDelete || menuOpen || (section.headerActionLoading ?? false)}
          {...(dragHandleProps ?? {})}
          secondaryAction={secondaryAction}
        >
          <ListItemButton
            selected={selected}
            onClick={handleSelectSection}
            onDoubleClick={handleEditSection}
            sx={{ paddingLeft: '10px' }}
            dense
          >
            <ListItemIcon sx={{ minWidth: 'auto', marginRight: '10px' }} onClick={handleToggle}>
              <OpenIcon className={open ? 'open' : ''} icon="chevron-right" />
            </ListItemIcon>
            <ListItemText
              sx={{
                minWidth: 0,
                '& .MuiListItemText-primary': { display: 'flex', columnGap: 2, minWidth: 0 },
              }}
            >
              <FaIconUvilo
                icon={section.icon}
                prefix={section.iconPrefix as IconPrefix | undefined}
                size="lg"
              />
              <TextEditable
                text={section.name}
                setText={setSectionName}
                disabled={!renameEnabled}
              />
              {section.secondaryText ? (
                <Typography variant="caption" sx={{ color: 'text.secondary', flexShrink: 0 }}>
                  {section.secondaryText}
                </Typography>
              ) : null}
              <Typography variant="caption" sx={{ opacity: 0.5, flexShrink: 0 }}>
                {itemsLoaded ? `(${section.items.length})` : ''}
              </Typography>
            </ListItemText>
          </ListItemButton>
        </HoverListItem>
        <Collapse in={open} timeout="auto" unmountOnExit={!isEmpty}>
          <List component="div" disablePadding>
            <List className="item-list" disablePadding>
              {itemsLoading && isEmpty ? <Spinner /> : null}
              {section.items.map((item, index) =>
                itemsDraggable ? (
                  <DraggableItem
                    key={itemDraggableId(droppableType, section.id, item.id)}
                    type={droppableType}
                    id={`${section.id}::${item.id}`}
                    index={index}
                  >
                    <Fragment>{renderRow(item)}</Fragment>
                  </DraggableItem>
                ) : (
                  <Fragment key={`${section.id}::${item.id}`}>{renderRow(item)}</Fragment>
                ),
              )}
            </List>
            {isEmpty ? (
              <ListItemText
                primary={emptyText ?? t('empty')}
                slotProps={{
                  primary: {
                    variant: 'caption',
                    align: 'center',
                  },
                }}
              />
            ) : null}
          </List>
        </Collapse>
      </LabelSectionContext.Provider>
    </Box>
  );
}
